import { AfterViewInit, Component, forwardRef, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService }                    from '@ngx-translate/core';
import { UIRouter }                            from '@uirouter/core';
import { Subscription }                        from 'rxjs';

import { ApiService }                        from '../../api/api.service';
import { CodeTypeModel }                     from '../../core/models/code-type';
import { EventBusService }                   from '../../core/services/event-bus.service';
import { InvoiceContactStatusComponent }     from '../../invoices/contact-status/invoice.contact-status.component';
import { JobRowExpandComponent }             from './transaction-center.job.row.expand.component';
import { MessagesService }                   from '../../messages/messages.service';
import { MessengerService }                  from '../../messages/messenger/messenger.service';
import { SessionService }                    from '../../sessions/session.service';
import { Table }                             from '../../table/types/table';
import { TableQueryParameters }              from '../../table/types/table-query-parameters';
import { TableService }                      from '../../table/table.service';
import { TransactionCenterFiltersModel }     from '../models/transaction-center.filters';
import { TransactionCenterJobModel }         from '../models/transaction-center.job';

@Component({
  selector: 'wor-tc-jobs',
  template: require('./transaction-center.jobs.component.html')
})
export class TransactionCenterJobsComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() constraints: Array<CodeTypeModel>;
  @Input() jobs: Array<TransactionCenterJobModel>;
  @Input() jobStatusOptions: Array<CodeTypeModel>;
  @Input() jobStatuses: Array<CodeTypeModel>;

  filters: TransactionCenterFiltersModel;
  tableSettings: Table;

  private company: any;
  private fleetCodeLabel: string;
  private regoLabel: string;
  private tableCacheKey: string;
  private subscription: Subscription;

  constructor (
    @Inject(forwardRef(() => ApiService)) private apiService: ApiService,
    @Inject(forwardRef(() => EventBusService))
    private eventBusService: EventBusService,
    @Inject(forwardRef(() => MessagesService)) private messagesService: MessagesService,
    @Inject(forwardRef(() => MessengerService)) private messengerService: MessengerService,
    @Inject(forwardRef(() => SessionService))
    private sessionService: SessionService,
    @Inject(forwardRef(() => TableService)) private tableService: TableService,
    private translateService: TranslateService,
    private uiRouter: UIRouter
  ) {}

  ngOnInit(): void {
    this.company        = this.sessionService.company();
    this.fleetCodeLabel = this.company.company_variable_fields.fleet_code_name || this.translateService.instant('VEHICLE_SPACE.FLEET_CODE');
    this.regoLabel      = this.company.company_variable_fields.plate_number_name || this.sessionService.countrySettings().rego_label;
    this.tableCacheKey  = this.uiRouter.stateService.current.data.tableCacheKey;

    this.filters = this.tableService.setFiltersFromCache(this.tableCacheKey, {
      postDate   : '*',
      constrainBy: '*',
      jobStatus  : '*',
      value      : ''
    });

    this.tableSettings = {
      broadcast: 'filterJobs',
      cache: { enabled: true, key: this.tableCacheKey },
      columns: [],
      empty: { text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_JOBS_FOUND') },
      pagination: {
        enabled: true,
        pageSize: this.tableService.getCachedPaging(this.tableCacheKey).pageSize || 10,
        pageSizes: [10, 25, 50],
        queryCount: () => this.getCount()
      },
      rows: {
        expand: {
          bindings: {
            outputs: {
              onEditClick : event => this.viewJob(event),
              onLaborClick: event => this.uiRouter.stateService.go('app.labor-items', { invoice_id: event.job.invoice.id }),
              onSMSClick  : event => this.sendSMS(event)
            }
          },
          enabled: true,
          factory: JobRowExpandComponent
        },
        highlight: { enabled: true },
        select: { enabled: true }
      },
      sorting: this.tableService.setSortingFromCache(this.tableCacheKey, { direction: 'desc', field: 'invoice.post_date' })
    };

    this.buildColumns();

    this.subscription = this.eventBusService.on<string>('searchFilterChanged').subscribe(value => {
      if (!this.uiRouter.stateService.transition) {
        this.filters.value = value;
        this.onFiltersChanged();
      }
    });
  }

  ngAfterViewInit(): void {
    const interval = setInterval(() => {
      if (typeof window['filterJobs'] === 'function') {
        clearInterval(interval);
        this.onFiltersChanged();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  onFiltersChanged(): void {
    if (window['filterJobs']) {
      window['filterJobs'](this.filters);
    }
  }

  get postDateDisplay(): string { return this.filters.postDate === '*' ? '' : this.filters.postDate; }
  set postDateDisplay(value: string) { this.filters.postDate = value || '*'; this.onFiltersChanged(); }

  private buildColumns(): void {
    this.tableSettings.columns = [
      { field: 'job.invoice.post_date', header: { text: this.translateService.instant('GENERAL_SPACE.FIELD.DATE') }, pipe: 'globalDate' },
      { field: 'job.customer.display_name', header: { text: this.translateService.instant('CUSTOMER_SPACE.CUSTOMER') } },
      { field: 'job.vehicle.make', header: { text: this.translateService.instant('VEHICLE_SPACE.MAKE') } },
      { field: 'job.vehicle.model', header: { text: this.translateService.instant('VEHICLE_SPACE.MODEL') } },
      { field: 'job.vehicle.plate_number', header: { text: this.regoLabel } }
    ];

    if (this.company.company_settings.show_fleet_code) {
      this.tableSettings.columns.push({
        classes: 'text-center',
        field: 'job.vehicle.fleet_code',
        header: { classes: 'text-center', text: this.fleetCodeLabel }
      });
    }

    this.tableSettings.columns.push(
      { classes: 'text-center', field: 'job.invoice.job_card_number', header: { classes: 'text-center', text: this.translateService.instant('MENU_SPACE.JOB_CARD') } },
      { classes: 'text-left', field: 'job.invoice.job_status', header: { classes: 'text-left', text: this.translateService.instant('GENERAL_SPACE.FIELD.STATUS') }, pipe: 'jobStatus', pipeArgs: [this.jobStatuses] },
      { classes: 'text-left', field: 'job.invoice.job_status_comment', header: { classes: 'text-left', text: this.translateService.instant('JS_SPACE.COLUMNS.STATUS_COMMENT') } },
      { component: { enabled: true, factory: InvoiceContactStatusComponent }, field: 'job.invoice', header: { classes: 'text-center', text: this.translateService.instant('GENERAL_SPACE.STATUS.CONTACTED') }, sortable: false },
      { classes: 'text-right', field: 'job.invoice.total', header: { classes: 'text-right', text: this.translateService.instant('GENERAL_SPACE.FIELD.TOTAL') }, pipe: 'globalCurrency' }
    );
  }

  getCount(): Promise<number> {
    return this.apiService.get('/job_center/count', [
      this.filters.value       || '*',
      this.filters.postDate    || '*',
      this.filters.constrainBy || '*',
      this.filters.jobStatus   || '*'
    ]);
  }

  getJobs(params: TableQueryParameters): Promise<any> {
    return this.apiService.stream('/job_center', [
      this.filters.value || '*',
      params.offset,
      params.pageSize,
      this.filters.postDate    || '*',
      this.filters.constrainBy || '*',
      this.filters.jobStatus   || '*',
      params.field.replace('job.', ''),
      params.direction
    ]).then((jobs: Array<TransactionCenterJobModel>) => {
      this.jobs = jobs;
      return jobs;
    });
  }

  sendSMS(row: any): void {
    this.messengerService.sms({
      name      : row.job.customer.display_name,
      send_to   : row.job.customer.mobile,
      invoice_id: row.job.invoice.id
    }).then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT'), 'success');
    });
  }

  viewJob(job: any): void {
    this.uiRouter.stateService.go('app.invoice', {
      customer_id: job.job.invoice.customer_id,
      id         : job.job.invoice.id,
      vehicle_id : job.job.invoice.vehicle_id
    });
  }
}
